/**
 * Re-exports from the entity layer. Canonical definitions live in
 * `entities/brief/model/constants.ts`; this file preserves the local
 * import path for existing consumers within the Briefing section.
 */
import type { BriefSortField, BriefSortOrder, BriefStatus } from '@/entities/brief';
import type { FilterChipOption } from '@/shared/ui/FilterChipPopover';
import { i18n } from '@/shared/i18n';
import type { SortChipOption } from '@/shared/ui/SortChipPopover';

export { BRIEF_TYPE_ICON, BRIEF_TYPE_LABEL } from '@/entities/brief';

/**
 * A briefs-list sort choice: a `SortChipPopover` option carrying the
 * `sortBy` + `sortOrder` tuple it maps to (resolved via
 * {@link resolveBriefSort}).
 */
interface BriefSortChoice extends SortChipOption {
  sortBy: BriefSortField;
  sortOrder: BriefSortOrder;
}

/**
 * Sort choices for the briefs list `SortChipPopover`.
 *
 * The endpoint default (`updated_at` DESC = "Last modified (newest first)") is
 * the chip's reset row, NOT an entry here — clearing the chip falls back to
 * that order, so listing it again would be redundant.
 *
 * Order: the default's sibling ("Last modified (oldest first)") leads so the
 * two time sorts read as a pair right under the pinned default, then the
 * remaining fields alphabetically (Name, Status, Type).
 */
// Labels are getters so each read resolves against the active locale; a plain
// `i18n.t(...)` here would freeze the English string at import time.
export const BRIEF_SORT_CHOICES: readonly BriefSortChoice[] = [
  {
    id: 'updated_at-asc',
    get label() {
      return i18n.t('projects:projectDetail.briefsConstants.lastModifiedOldestFirst');
    },
    direction: 'asc',
    sortBy: 'updated_at',
    sortOrder: 'ASC',
  },
  {
    id: 'title-asc',
    get label() {
      return i18n.t('projects:projectDetail.briefsConstants.nameAZ');
    },
    direction: 'asc',
    sortBy: 'title',
    sortOrder: 'ASC',
  },
  {
    id: 'title-desc',
    get label() {
      return i18n.t('projects:projectDetail.briefsConstants.nameZA');
    },
    direction: 'desc',
    sortBy: 'title',
    sortOrder: 'DESC',
  },
  {
    id: 'status-asc',
    get label() {
      return i18n.t('projects:projectDetail.briefsConstants.statusAZ');
    },
    direction: 'asc',
    sortBy: 'status',
    sortOrder: 'ASC',
  },
  {
    id: 'brief_type-asc',
    get label() {
      return i18n.t('projects:projectDetail.briefsConstants.typeAZ');
    },
    direction: 'asc',
    sortBy: 'brief_type',
    sortOrder: 'ASC',
  },
];

/**
 * Resolves a {@link BRIEF_SORT_CHOICES} id back to its `sortBy` + `sortOrder`
 * tuple. Returns `null` for `null` or an unknown id, so the caller omits the
 * sort params and the endpoint applies its default (`updated_at` DESC).
 */
export function resolveBriefSort(
  id: string | null,
): { sortBy: BriefSortField; sortOrder: BriefSortOrder } | null {
  if (id === null) return null;
  const choice = BRIEF_SORT_CHOICES.find((c) => c.id === id);
  return choice ? { sortBy: choice.sortBy, sortOrder: choice.sortOrder } : null;
}

/**
 * Status filter options for the briefs list `FilterChipPopover`. The endpoint
 * accepts `status[]`; the toolbar drives this single-select and maps the
 * chosen value to a one-element array (or omits it for "All statuses").
 *
 * Label note: `submitted` renders as "Ready" — UI-only relabel,
 * the BE enum value passed back to the endpoint is still `submitted`.
 */
// Labels are getters so each read resolves against the active locale; a plain
// `i18n.t(...)` here would freeze the English string at import time.
export const BRIEF_STATUS_FILTER_OPTIONS: readonly FilterChipOption<BriefStatus>[] = [
  {
    value: 'draft',
    get label() {
      return i18n.t('projects:projectDetail.briefsConstants.draft');
    },
  },
  {
    value: 'submitted',
    get label() {
      return i18n.t('projects:projectDetail.briefsConstants.ready');
    },
  },
];

/**
 * Intro description for the Briefs page header AND its default empty state
 * (which replicates the header — title + description — mirroring the Projects
 * list). Shared from one place so the two never drift.
 */
// A function, not a const: resolving at call time is what lets the copy follow a
// language change instead of freezing at import.
export const briefsIntroDescription = (): string =>
  i18n.t('projects:projectDetail.briefsConstants.createBriefsAgenticallyFromYour');
